import { useLocation, useNavigate } from 'react-router-dom';
import { Home, MoreHorizontal } from 'lucide-react';

import {
  adminRoutes,
  getCategoryByPath,
  getCustomizedAccessibleCategoryIds,
} from '../navigation/adminRoutes';
import { getRememberedCategoryPath } from '../navigation/adminPlaceMemory';
import { getAdminUser } from '../navigation/sectionAccess';
import { useAdminNavigation } from '../navigation/useAdminNavigation';

import { cn } from '@/lib/utils';

const AdminMobileBottomNav = ({ canAccessSection }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    mobileDrawerOpen,
    setMobileDrawerOpen,
    navigationPreferences = {},
  } = useAdminNavigation();
  const currentCategoryId = getCategoryByPath(location.pathname);
  const adminUser = getAdminUser();

  const visibleCockpits = getCustomizedAccessibleCategoryIds(
    canAccessSection,
    navigationPreferences,
  );

  // Lorenz uses a five-slot mobile strip: four direct destinations plus More.
  // Dashboard occupies the overview slot, leaving three primary cockpits.
  const primaryCockpits = visibleCockpits.slice(0, 3);

  // Build nav items: Dashboard + three cockpit landing pages + More.
  const navItems = [
    { id: '_dashboard', label: 'Dashboard', icon: Home, path: '/admin' },
    ...primaryCockpits.map((categoryId) => {
      const category = adminRoutes[categoryId];
      return {
        id: categoryId,
        label: category.shortLabel,
        fullLabel: category.label,
        icon: category.icon,
        path: getRememberedCategoryPath(categoryId, adminUser, canAccessSection),
      };
    }),
    { id: '_more', label: 'More', fullLabel: 'More cockpits', icon: MoreHorizontal },
  ];

  const handleNavClick = (item) => {
    if (item.id === '_more') {
      setMobileDrawerOpen(true);
    } else {
      navigate(item.path);
    }
  };

  return (
    // The home-indicator inset pads below the 4rem row, so it never eats into
    // the touch targets (OWZ-72).
    <nav className="fixed bottom-0 left-0 right-0 z-50 admin-mobile-bottom-nav safe-area-pb">
      <div className="flex items-stretch h-16">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.id === '_dashboard'
            ? location.pathname.replace(/\/+$/, '') === '/admin'
            : item.id === '_more'
              ? mobileDrawerOpen || (!!currentCategoryId && !primaryCockpits.includes(currentCategoryId))
              : currentCategoryId === item.id;

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleNavClick(item)}
              className={cn(
                'flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors relative',
                isActive
                  ? 'text-amber-400 bg-background/5'
                  : 'text-stone-400 hover:text-stone-200'
              )}
              aria-label={item.fullLabel || item.label}
              aria-current={isActive ? 'page' : undefined}
              aria-expanded={item.id === '_more' ? mobileDrawerOpen : undefined}
            >
              <Icon className={cn('h-6 w-6', isActive && 'text-amber-400')} />
              <span className="text-[11px] font-medium leading-tight">{item.label}</span>
              {isActive && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-amber-400 rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default AdminMobileBottomNav;
